import React, { useCallback, useEffect, useRef,useState } from 'react';
import './GuessSeason.css';

/** 季节类型 */
type Season = 'spring' | 'summer' | 'autumn' | 'winter';

/** 媒体类型 */
type MediaType = 'video' | 'gif';

/** 单道题目配置 */
interface QuizItem {
  /** 题目前缀文案 */
  prefix: string;
  /** 高亮关键词 */
  highlight: string;
  /** 高亮词颜色 */
  highlightColor: string;
  /** 正确答案 */
  answer: Season;
  /** 媒体地址 */
  mediaUrl: string;
  /** 媒体类型 */
  mediaType: MediaType;
  /** 媒体封面 */
  poster?: string;
  /** 环境音效（Mixkit 等免费素材通常无内嵌音轨，需单独配置） */
  audioUrl?: string;
}

/** 季节按钮配置 */
interface SeasonOption {
  season: Season;
  label: string;
  className: string;
}

/** 季节英文单词配置 */
interface SeasonWord {
  /** 英文单词 */
  word: string;
  /** 展示颜色 */
  color: string;
}

/** 四季英文单词映射 */
const SEASON_WORDS: Record<Season, SeasonWord> = {
  spring: { word: 'Spring', color: '#689f38' },
  summer: { word: 'Summer', color: '#1976d2' },
  autumn: { word: 'Autumn', color: '#e65100' },
  winter: { word: 'Winter', color: '#546e7a' },
};

const QUIZ_ITEMS: QuizItem[] = [
  {
    prefix: 'The weather is',
    highlight: 'warm',
    highlightColor: '#ff8c00',
    answer: 'spring',
    mediaUrl: 'https://assets.mixkit.co/videos/48889/48889-720.mp4',
    mediaType: 'video',
    poster: 'https://assets.mixkit.co/videos/48889/48889-thumb-720-0.jpg',
    audioUrl: 'https://assets.mixkit.co/active_storage/sfx/2472/2472-preview.mp3',
  },
  {
    prefix: 'The weather is',
    highlight: 'hot',
    highlightColor: '#e53935',
    answer: 'summer',
    mediaUrl: 'https://assets.mixkit.co/videos/51480/51480-720.mp4',
    mediaType: 'video',
    poster: 'https://assets.mixkit.co/videos/51480/51480-thumb-720-0.jpg',
    audioUrl: 'https://assets.mixkit.co/active_storage/sfx/1208/1208-preview.mp3',
  },
  {
    prefix: 'The leaves are',
    highlight: 'falling',
    highlightColor: '#e65100',
    answer: 'autumn',
    mediaUrl: 'https://assets.mixkit.co/videos/10050/10050-720.mp4',
    mediaType: 'video',
    poster: 'https://assets.mixkit.co/videos/10050/10050-thumb-720-0.jpg',
    audioUrl: 'https://assets.mixkit.co/active_storage/sfx/2608/2608-preview.mp3',
  },
  {
    prefix: 'The weather is',
    highlight: 'cold',
    highlightColor: '#0277bd',
    answer: 'winter',
    mediaUrl: 'https://assets.mixkit.co/videos/9431/9431-720.mp4',
    mediaType: 'video',
    poster: 'https://assets.mixkit.co/videos/9431/9431-thumb-720-0.jpg',
    audioUrl: 'https://assets.mixkit.co/active_storage/sfx/1471/1471-preview.mp3',
  },
];

const SEASON_OPTIONS: SeasonOption[] = [
  { season: 'spring', label: '春', className: 'guess-season-option--spring' },
  { season: 'summer', label: '夏', className: 'guess-season-option--summer' },
  { season: 'autumn', label: '秋', className: 'guess-season-option--autumn' },
  { season: 'winter', label: '冬', className: 'guess-season-option--winter' },
];

/**
 * 渲染季节图标
 * @param season - 季节
 */
const SeasonIcon: React.FC<{ season: Season }> = ({ season }) => {
  const icons: Record<Season, React.ReactNode> = {
    spring: (
      <svg className="guess-season-option-icon" viewBox="0 0 32 32" aria-hidden="true">
        <circle cx="16" cy="16" r="5" fill="currentColor" />
        <ellipse cx="16" cy="7" rx="4" ry="7" fill="currentColor" />
        <ellipse cx="16" cy="25" rx="4" ry="7" fill="currentColor" />
        <ellipse cx="7" cy="16" rx="7" ry="4" fill="currentColor" />
        <ellipse cx="25" cy="16" rx="7" ry="4" fill="currentColor" />
      </svg>
    ),
    summer: (
      <svg className="guess-season-option-icon" viewBox="0 0 32 32" aria-hidden="true">
        <circle cx="16" cy="16" r="8" fill="currentColor" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <rect
            key={angle}
            x="14.5"
            y="1"
            width="3"
            height="6"
            rx="1.5"
            fill="currentColor"
            transform={`rotate(${angle} 16 16)`}
          />
        ))}
      </svg>
    ),
    autumn: (
      <svg className="guess-season-option-icon" viewBox="0 0 32 32" aria-hidden="true">
        <path
          d="M16 4 C10 4 6 10 6 16 C6 22 10 28 16 28 C22 28 26 22 26 16 C26 10 22 4 16 4 Z M16 8 C18 12 20 14 22 16 C20 18 18 20 16 24 C14 20 12 18 10 16 C12 14 14 12 16 8 Z"
          fill="currentColor"
        />
      </svg>
    ),
    winter: (
      <svg className="guess-season-option-icon" viewBox="0 0 32 32" aria-hidden="true">
        <path d="M16 4 L18 14 L28 10 L20 18 L30 22 L18 22 L20 32 L16 24 L12 32 L14 22 L2 22 L12 18 L4 10 L14 14 Z" fill="currentColor" />
      </svg>
    ),
  };

  return <>{icons[season]}</>;
};

/**
 * 渲染题目媒体（视频或 GIF），并播放季节环境音
 * @param item - 当前题目
 */
const QuizMedia: React.FC<{ item: QuizItem }> = ({ item }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioUnlockedRef = useRef(false);

  const playAmbient = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !item.audioUrl) return;
    audio.play().catch(() => {});
  }, [item.audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.load();
    }

    if (audioUnlockedRef.current) {
      playAmbient();
      return undefined;
    }

    const unlockAudio = () => {
      audioUnlockedRef.current = true;
      playAmbient();
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });
    return () => window.removeEventListener('pointerdown', unlockAudio);
  }, [item.mediaUrl, item.audioUrl, playAmbient]);

  if (item.mediaType === 'video') {
    return (
      <>
        <video
          key={item.mediaUrl}
          src={item.mediaUrl}
          poster={item.poster}
          autoPlay
          loop
          muted
          playsInline
        />
        {item.audioUrl && (
          <audio ref={audioRef} key={item.audioUrl} src={item.audioUrl} loop preload="auto" />
        )}
      </>
    );
  }

  return <img key={item.mediaUrl} src={item.mediaUrl} alt={item.highlight} />;
};

/**
 * 猜季节互动测验组件
 */
const GuessSeason: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [answered, setAnswered] = useState(false);

  const currentItem = QUIZ_ITEMS[currentIndex];

  /**
   * 处理季节选项点击
   * @param season - 用户选择的季节
   */
  const handleSelect = useCallback((season: Season) => {
    setSelectedSeason(season);
    setAnswered(true);
  }, []);

  /**
   * 切换到下一题
   */
  const handleNext = useCallback(() => {
    if (currentIndex < QUIZ_ITEMS.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedSeason(null);
      setAnswered(false);
    }
  }, [currentIndex]);

  /**
   * 切换到上一题
   */
  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setSelectedSeason(null);
      setAnswered(false);
    }
  }, [currentIndex]);

  /**
   * 获取选项按钮样式类名
   * @param season - 季节
   */
  const getOptionClassName = (season: Season): string => {
    const base = SEASON_OPTIONS.find((o) => o.season === season)?.className ?? '';
    const classes = ['guess-season-option', base];

    if (selectedSeason === season) {
      classes.push('guess-season-option--selected');
    }

    if (answered) {
      const isAnswerCorrect = selectedSeason === currentItem.answer;
      if (isAnswerCorrect && season === currentItem.answer) {
        classes.push('guess-season-option--correct');
      } else if (selectedSeason === season && !isAnswerCorrect) {
        classes.push('guess-season-option--wrong');
      }
    }

    return classes.join(' ');
  };

  /** 底部反馈文案 */
  const feedbackText = answered
    ? selectedSeason === currentItem.answer
      ? '答对了！继续下一题吧 🎉'
      : `再想想～正确答案是「${SEASON_OPTIONS.find((o) => o.season === currentItem.answer)?.label}」`
    : '观看视频，点击页面任意处开启声音';

  return (
    <div className="guess-season-page">
      <span className="guess-season-deco guess-season-deco--flower-1" aria-hidden="true">
        ✿
      </span>
      <span className="guess-season-deco guess-season-deco--flower-2" aria-hidden="true">
        ✿
      </span>
      <span className="guess-season-deco guess-season-deco--heart" aria-hidden="true">
        ♥
      </span>

      <header className="guess-season-header">
        <h1 className="guess-season-title">
          <span className="guess-season-title-en">Guess the Season</span>
          <span className="guess-season-title-zh">猜季节</span>
        </h1>
      </header>

      <main className="guess-season-card">
        <span className="guess-season-card-deco guess-season-card-deco--star-1" aria-hidden="true">
          ⭐
        </span>
        <span className="guess-season-card-deco guess-season-card-deco--star-2" aria-hidden="true">
          ✨
        </span>
        <span className="guess-season-card-deco guess-season-card-deco--cloud" aria-hidden="true">
          ☁
        </span>

        <section className="guess-season-content">
          <div className="guess-season-question">
            The weather is
            <br />
            {answered && selectedSeason === currentItem.answer ? (
              <span
                className="guess-season-highlight guess-season-highlight--revealed"
                style={{ color: SEASON_WORDS[currentItem.answer].color }}
              >
                {SEASON_WORDS[currentItem.answer].word}
              </span>
            ) : (
              <span className="guess-season-highlight guess-season-highlight--hidden" aria-hidden="true">
                <span className="guess-season-word-placeholder" />
              </span>
            )}
          </div>

          <div className="guess-season-media">
            <QuizMedia item={currentItem} />
          </div>
        </section>

        <section
          className={`guess-season-options${answered ? ' guess-season-options--answered' : ''}`}
        >
          {SEASON_OPTIONS.map(({ season, label }) => {
            const isSelected = selectedSeason === season;
            const isAnswerCorrect = answered && selectedSeason === currentItem.answer;
            const isCorrect = isAnswerCorrect && season === currentItem.answer;
            const isWrong = answered && isSelected && !isAnswerCorrect;

            return (
              <button
                key={season}
                type="button"
                className={getOptionClassName(season)}
                onClick={() => handleSelect(season)}
                aria-pressed={isSelected}
              >
                {isCorrect && (
                  <span
                    className="guess-season-option-badge guess-season-option-badge--correct"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                )}
                {isWrong && (
                  <span
                    className="guess-season-option-badge guess-season-option-badge--wrong"
                    aria-hidden="true"
                  >
                    ✗
                  </span>
                )}
                <SeasonIcon season={season} />
                <span>{label}</span>
              </button>
            );
          })}
        </section>

        <footer className="guess-season-footer">
          <span className="guess-season-footer-icon">+</span>
          <span>{feedbackText}</span>
        </footer>
      </main>

      <nav className="guess-season-nav">
        <button
          type="button"
          className="guess-season-nav-btn"
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          上一题
        </button>
        <button
          type="button"
          className="guess-season-nav-btn"
          onClick={handleNext}
          disabled={currentIndex === QUIZ_ITEMS.length - 1}
        >
          下一题
        </button>
      </nav>
    </div>
  );
};

export default GuessSeason;
